import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup, Textarea } from "@cofano/cck";
import { SavePurchaseCharterDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import { RelationType } from "__generated__/graphql";
import { intl } from "LocaleWrapper";
import FormField from "components/cck/FormField";
import { FormDialog } from "components/dialogs/Dialogs";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import DatePicker from "components/cck/DatePicker";
import { formatCharterForMutation } from "../../dossier/utils";
import { PurchaseCharterFromQuery } from "../../PurchaseCharterDetails";
import {
	purchaseCharterSchema,
	PurchaseCharterSchemaInput,
	PurchaseCharterSchemaOutput,
} from "view/charters/utils/charter.schema";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import { CharterSubtype, CharterType } from "__generated__/enums";

type CreatePurchaseCharterDialogProps = {
	open: boolean;
	onClose: () => void;
	charter?: PurchaseCharterFromQuery;
	refetch?: (charterId: number) => void;
};

const PurchaseCharterCreateEditDialog = ({ open, onClose, charter, refetch }: CreatePurchaseCharterDialogProps) => {

	const defaultValues = {
		subtype: CharterSubtype.Spot,
		creditor: null,
		debtor: null,
		validFrom: null,
		validUntil: null,
		customerReference: "",
	};

	const { control, handleSubmit, formState, reset } = useForm<PurchaseCharterSchemaInput, unknown, PurchaseCharterSchemaOutput>({
		resolver: zodResolver(purchaseCharterSchema),
		defaultValues: defaultValues,
	});

	const subtype = useWatch({ control, name: "subtype" });

	const [ saveCharter, { loading } ] = useMutation(SavePurchaseCharterDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const onSave = (data: PurchaseCharterSchemaOutput) => {
		const input = formatCharterForMutation({
			id: charter?.id || 0,
			charterNumber: charter?.charterNumber || "",
			type: CharterType.Purchase,
			subtype: data.subtype,
			creditorId: data.creditor?.id || 0,
			debtorId: data.debtor?.id || 0,
			brokerId: undefined,
			relationType: charter?.relationType ?? undefined,
			contacts: [],
			minimumShipNominationDays: null,
			shipNominationPeriodRegime: null,
			shortNotice: false,
			customerNotes: "",
			internalNotes: "",
			customerReference: data.customerReference,
			blDate: undefined,
			contract: charter?.contract ?? undefined,
		});

		saveCharter({
			variables: {
				input: {
					...input,
					validFrom: data.validFrom ?? undefined,
					validUntil: data.validUntil ?? undefined,
				},
			},
		}).then((response) => {
			onClose();
			if (response.data) {
				refetch?.(response.data.saveCharter.id);
			}
		});
	};

	useEffect(() => {
		if (open && charter) {
			reset({
				subtype: charter.subtype,
				creditor: charter.creditor,
				debtor: charter.debtor,
				validFrom: charter.validFrom ?? null,
				validUntil: charter.validUntil ?? null,
				customerReference: charter.customerReference || "",
			});
		}
	}, [ open, charter, reset ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "PURCHASE_CHARTER" })}
			form='purchaseCharterForm'
			loading={loading}>
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(onSave)(event)} id="purchaseCharterForm">
					<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="subtype"
							control={control}
							render={({ field }) => (
								<CharterSubtypeSelect
									{...field}
									disabled={!!charter}
									disableClear
								/>
							)}
						/>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CONTRACTOR" })}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="creditor">
							<Controller
								name="creditor"
								control={control}
								render={({ field }) => (
									<RelationSelect
										{ ...field}
										types={[ RelationType.ApplicationRelation, RelationType.Operator, RelationType.ShipManager, RelationType.Skipper ]}
									/>
								)} />
						</FormField>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CUSTOMER" })}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name="debtor">
							<Controller
								name="debtor"
								control={control}
								render={({ field }) => (
									<RelationSelect
										{ ...field}
										types={[ RelationType.Customer, RelationType.ApplicationRelation ]} />
								)} />
						</FormField>
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "VALID_FROM" })} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="validFrom"
							control={control}
							render={({ field }) => <DatePicker {...field} disabled={!!charter && subtype === CharterSubtype.Spot} />} />
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "VALID_UNTIL" })} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="validUntil"
							control={control}
							render={({ field }) => <DatePicker {...field} disabled={!!charter && subtype === CharterSubtype.Spot} />} />
					</FormGroup>

					<FormGroup label={intl.formatMessage({ id: "CUSTOMER_REFERENCE" })} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="customerReference"
							control={control}
							render={({ field }) => <Textarea {...field} value={field.value || undefined } />} />
					</FormGroup>
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

export default PurchaseCharterCreateEditDialog;
